import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { ConsoleClient } from '../api/client'
import { ConnectionManager } from '../auth/pairing'
import type { AccessSession, ConnectionState } from '../auth/types'
import { RealtimeConsoleEngine, type RealtimeClient } from '../realtime/engine'
import type { RealtimeState } from '../realtime/state'
import type { ConsoleViewState } from './state'

type BrowserLocation = Pick<Location, 'hash' | 'origin' | 'pathname' | 'search'>
type BrowserHistory = Pick<History, 'replaceState'>
type RuntimeManager = Pick<ConnectionManager, 'pairFromFragment' | 'restore'>
type RuntimeEngine = {
  subscribe(listener: (state: RealtimeState) => void): () => void
  start(): Promise<void>
  stop(): void
}

export type ConsoleRuntimeDependencies = {
  manager?: RuntimeManager
  createEngine?: (session: AccessSession) => RuntimeEngine
  location?: BrowserLocation
  history?: BrowserHistory
  deviceLabel?: string
  now?: () => number
  setTimer?: (callback: () => void, delayMs: number) => unknown
  clearTimer?: (handle: unknown) => void
}

type Props = {
  children: (state: ConsoleViewState) => ReactNode
  dependencies?: ConsoleRuntimeDependencies
}

const browserSetTimer = (callback: () => void, delayMs: number): unknown =>
  window.setTimeout(callback, delayMs)

const browserClearTimer = (handle: unknown): void =>
  window.clearTimeout(handle as number)

function defaultEngine(session: AccessSession): RuntimeEngine {
  const client: RealtimeClient = new ConsoleClient(
    session.connection.origin,
    () => session.accessToken,
  )
  return new RealtimeConsoleEngine(client, session.connection.origin)
}

function ConnectionNotice({
  state,
  booting,
  retry,
}: {
  state: ConnectionState
  booting: boolean
  retry: () => void
}) {
  if (booting || state.status === 'pairing' || state.status === 'restoring') {
    return (
      <main className="connection-screen">
        <p className="eyebrow">Terminal MCP</p>
        <h1>Connecting Console</h1>
        <p className="muted">Restoring the paired browser session and loading a server snapshot.</p>
      </main>
    )
  }

  const retryable = state.status === 'error' && state.operation === 'restore' && state.retryable
  const detail =
    state.status === 'revoked'
      ? 'This browser pairing was revoked on the server.'
      : state.status === 'expired'
        ? 'The stored browser pairing has expired.'
        : state.status === 'error'
          ? 'Connection failed: ' + state.message + '.'
          : 'Open a fresh Terminal MCP pairing link in this browser to connect.'

  return (
    <main className="connection-screen">
      <p className="eyebrow">Terminal MCP</p>
      <h1>Console is not connected</h1>
      <p className="muted">{detail}</p>
      {retryable && (
        <button className="primary-button" type="button" onClick={retry}>
          Retry connection
        </button>
      )}
    </main>
  )
}

export function ConsoleRuntime({ children, dependencies = {} }: Props) {
  const managerRef = useRef<RuntimeManager | null>(null)
  if (managerRef.current === null) managerRef.current = dependencies.manager ?? new ConnectionManager()

  const createEngine = dependencies.createEngine ?? defaultEngine
  const location = dependencies.location ?? window.location
  const history = dependencies.history ?? window.history
  const now = dependencies.now ?? Date.now
  const setTimer = dependencies.setTimer ?? browserSetTimer
  const clearTimer = dependencies.clearTimer ?? browserClearTimer

  const [connection, setConnection] = useState<ConnectionState>({ status: 'unpaired' })
  const [realtime, setRealtime] = useState<RealtimeState | null>(null)
  const [booting, setBooting] = useState(true)
  const bootPromise = useRef<Promise<ConnectionState> | null>(null)

  const restore = useCallback(async () => {
    setBooting(true)
    const next = await managerRef.current!.restore()
    setConnection(next)
    setBooting(false)
  }, [])

  useEffect(() => {
    if (bootPromise.current === null) {
      const manager = managerRef.current!
      bootPromise.current =
        location.hash.length > 1
          ? manager.pairFromFragment(location, history, dependencies.deviceLabel ?? 'Web Console')
          : manager.restore()
    }

    let subscribed = true
    void bootPromise.current.then((next) => {
      if (!subscribed) return
      setConnection(next)
      setBooting(false)
    })

    return () => {
      subscribed = false
    }
  }, [dependencies.deviceLabel, history, location])

  useEffect(() => {
    if (connection.status !== 'connected') return

    const engine = createEngine(connection.session)
    const unsubscribe = engine.subscribe(setRealtime)
    void engine.start()

    return () => {
      unsubscribe()
      engine.stop()
    }
  }, [connection, createEngine])

  useEffect(() => {
    if (connection.status !== 'connected') return
    const refreshAt = connection.session.accessExpiresAt - 60_000
    const delay = Math.max(1_000, refreshAt - now())
    const handle = setTimer(() => void restore(), delay)
    return () => clearTimer(handle)
  }, [clearTimer, connection, now, restore, setTimer])

  if (connection.status !== 'connected') {
    return <ConnectionNotice state={connection} booting={booting} retry={() => void restore()} />
  }

  if (realtime?.snapshot === null || realtime === null) {
    return (
      <main className="connection-screen">
        <p className="eyebrow">Terminal MCP</p>
        <h1>Loading server state</h1>
        <p className="muted">{connection.session.connection.origin}</p>
      </main>
    )
  }

  return children({
    snapshot: realtime.snapshot,
    connectionStatus: realtime.status,
  })
}
